import '@widesk-ui/styles/style.scss';
import '@/styles/style.scss';

import 'dayjs/locale/fr';

import ApiModel                               from '@widesk-core/models/ApiModel';
import AuthProvider                           from '@widesk-ui/contexts/AuthContext';
import ConfigProvider                         from 'antd/lib/config-provider';
import LocaleCollection                       from '@widesk-core/models/LocaleCollection';
import LoginCheckModel                        from '@/models/services/account/LoginCheckModel';
import React                                  from 'react';
import RouterProvider                         from '@/RouterProvider';
import ThemeModeContext                       from '@widesk-ui/contexts/ThemeModeContext';
import appStore                               from '@/stores/appStore';
import authStore                              from '@widesk-core/stores/authStore';
import dayjs                                  from 'dayjs';
import errorsFr                               from '@/errorTranslations/fr.json';
import locale                                 from 'antd/locale/fr_FR';
import localizedFormat                        from 'dayjs/plugin/localizedFormat';
import { getAntdTheme, NAV_BG }               from '@/theme';
import utc                                    from 'dayjs/plugin/utc';
import { ThemeMode }                          from '@widesk-ui/contexts/ThemeModeContext';
import { getIdFromUrn }                       from '@widesk-core/tools/identifier';
import { setErrorsTranslations }              from '@widesk-core/tools/getTextAndTranslateFromError';
import { onLogout }                           from './tools/logout';
import { message }                            from '@widesk-ui/hooks/useMessage';
import MercureTokenRequest                    from '@models/notification/MercureTokenRequest';
import { notificationsSidebar }               from '@stonecode/notifications-sidebar';
import useSyncNotificationCenterTheme         from '@widesk-ui/hooks/useSyncNotificationCenterTheme';
import userMercureListener                    from '@/services/userMercureListener';
import { requestTokenRefresh }                from '@/helpers/tokenRefresh';

dayjs.locale('fr');
dayjs.extend(localizedFormat);
dayjs.extend(utc);

setErrorsTranslations(errorsFr);

ApiModel.getResolvableCollection = () => LocaleCollection;

const LOCALSTORAGE_THEME_KEY = 'theme_mode';

// Doit être monté à l'intérieur du ConfigProvider pour avoir accès au Context antd (useTheme).
function NotificationCenterThemeSync() {
	useSyncNotificationCenterTheme();
	return null;
}

export default function App() {
	const [mode, setMode] = React.useState<ThemeMode>(
		localStorage.getItem(LOCALSTORAGE_THEME_KEY) as ThemeMode || 'light',
	);

	// On sauvegarde le mode en localStorage quand il change
	React.useEffect(() => localStorage.setItem(LOCALSTORAGE_THEME_KEY, mode), [mode]);

	React.useEffect(() => {
		document.documentElement.style.setProperty('--nav-bg', NAV_BG[mode]);
	}, [mode]);

	return (
		<ThemeModeContext.Provider value={{ mode, setMode }}>
			<ConfigProvider
				locale={locale}
				theme={getAntdTheme(mode)}
			>
				<NotificationCenterThemeSync />

				<AuthProvider
					onLogin={async (username, password) => {
						const loginCheck = new LoginCheckModel();
						await loginCheck.patch({ password, username });
						return [loginCheck.token, loginCheck.refreshToken];
					}}
					onLogout={onLogout}
					onLogged={async () => { // L'application charge tant que cette méthode n'est pas terminée
						authStore.setRefreshTokenRequest(requestTokenRefresh);

						const userId = getIdFromUrn(authStore.tokenData['impersonate.urn'] ?? authStore.tokenData['owner.urn']);

						if (userId) {
							await appStore.user.setId(userId).fetch();

							void userMercureListener.start();

							const services = window.APP_CONFIG?.services || [];
							const notificationService = services.find(s => s.id === 'notification');
							const mercureService = services.find(s => s.id === 'mercure');

							await notificationsSidebar.initialize({
								api: {
									endpoint: notificationService?.configurations.api_endpoint as `https://${string}.${string}/${string}`,
									resourceUrn: appStore.user.urn,
									token: authStore.token,
								},
								containerId: 'root',
								mercure: {
									endpoint: mercureService?.configurations.api_endpoint as `https://${string}.${string}/${string}`,
									getToken: async (subscriberUrn: Urn) => {
										const test = new MercureTokenRequest();
										await test.patch({
											subscriber: subscriberUrn,
											transportTypeReference: `studio_notification_center`,
										});

										return test.token;
									},
								},
								options: {
									filter: true,
									minimizable: true,
									readOnLinkClick: true,
								},
								notify: (content, type) => message.open({ content, type, duration: 4 }),
							});
						}
					}}
				>
					<RouterProvider />
				</AuthProvider>
			</ConfigProvider>
		</ThemeModeContext.Provider>
	);
}
